import { maxLength, notEmail } from '@seek/validators-js';
import { useTranslations } from '@vocab/react';
import { Stack, Box, FieldLabel } from 'braid-design-system';
import React, {
  type FormEvent,
  forwardRef,
  type Ref,
  useImperativeHandle,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { EVENT_NAME_INTERACTION } from 'src/app/analytics';
import { formFieldObject } from 'src/app/constants/formDataFields';
import getTrackingAdapter from 'src/app/dataAnalytics';
import { KEY_SELLING_POINT_PRESSED } from 'src/app/dataAnalytics/schema/constants';
import { sendContentSafetyTrackingEvents } from 'src/app/dataAnalytics/utils/sendContentSafetyTrackingEvents';
import { sendPerUnitBillingValidationTrackingEvents } from 'src/app/dataAnalytics/utils/sendPerUnitBillingValidationTrackingEvents';
import {
  FEATURE_DATA_ANALYTICS,
  FEATURE_FILTER_CONTENT,
  FEATURE_PER_UNIT_BILLING,
  useFeatureToggleData,
} from 'src/app/featureToggle';
import { actionUpdateKeySellingPoint1 } from 'src/app/state/modules/formData/keySellingPoint1';
import { actionUpdateKeySellingPoint2 } from 'src/app/state/modules/formData/keySellingPoint2';
import { actionUpdateKeySellingPoint3 } from 'src/app/state/modules/formData/keySellingPoint3';
import { sendLinkEvent } from 'src/app/utils/analytics';
import { log } from 'src/app/utils/logging';
import {
  type Validators,
  filterContent,
  validateFields,
} from 'src/app/utils/validation';
import { ProductFeatureBadge } from 'src/app/views/components/ProductFeatureBadge';
import { useConfig } from 'src/app/views/hooks/useConfig';
import { TextFieldWithCounter } from 'src/app/views/shared/TextFieldWithCounter';
import type AppState from 'src/types/appState';
import type { FormFieldState, KeySellingPointsRef } from 'src/types/formFields';
import type { TealiumEventData, TrackingEvent } from 'src/types/trackingData';

import translations from './.vocab';

const ANALYTICS_LINK_NAME_KSP1 = 'ecaja-key-selling-points1-clicked';
const ANALYTICS_OBJECTINTERACTION_KSP1 = 'key-selling-point-1';
const ANALYTICS_LINK_NAME_KSP2 = 'ecaja-key-selling-points2-clicked';
const ANALYTICS_OBJECTINTERACTION_KSP2 = 'key-selling-point-2';
const ANALYTICS_LINK_NAME_KSP3 = 'ecaja-key-selling-points3-clicked';
const ANALYTICS_OBJECTINTERACTION_KSP3 = 'key-selling-point-3';

const KSP_FIELD_ID_1 = 'KEY_SELLING_POINT_FIELD_ID_1';
const KSP_FIELD_ID_2 = 'KEY_SELLING_POINT_FIELD_ID_2';
const KSP_FIELD_ID_3 = 'KEY_SELLING_POINT_FIELD_ID_3';

const KEY_SELLING_POINTS_MAX_LENGTH = 80;

export const KeySellingPoints = forwardRef(
  (_props, ref: Ref<KeySellingPointsRef>) => {
    const {
      keySellingPoint1,
      keySellingPoint2,
      keySellingPoint3,
      tealium,
      jobLocationCountry,
      hirerNation,
    } = useSelector((state: AppState) => ({
      keySellingPoint1: state.formData.keySellingPoint1,
      keySellingPoint2: state.formData.keySellingPoint2,
      keySellingPoint3: state.formData.keySellingPoint3,
      tealium: state.tealium,
      jobLocationCountry: state.session.jobLocationCountry,
      hirerNation: state.session?.hirerAccount?.nation,
    }));

    const { getLanguage } = useConfig();
    const { t } = useTranslations(translations);
    const isPageAnalyticsEnabled = useFeatureToggleData(FEATURE_DATA_ANALYTICS);
    const country = (jobLocationCountry ?? hirerNation ?? 'AU').toLowerCase();
    const isFilterContentEnabled = useFeatureToggleData(FEATURE_FILTER_CONTENT);
    const isPerUnitBilling = useFeatureToggleData(FEATURE_PER_UNIT_BILLING);

    const [keySellingPoint1Input, setKeySellingPoint1Input] =
      useState<FormFieldState>({
        ...formFieldObject,
        value: keySellingPoint1 ?? '',
      });
    const [keySellingPoint2Input, setKeySellingPoint2Input] =
      useState<FormFieldState>({
        ...formFieldObject,
        value: keySellingPoint2 ?? '',
      });
    const [keySellingPoint3Input, setKeySellingPoint3Input] =
      useState<FormFieldState>({
        ...formFieldObject,
        value: keySellingPoint3 ?? '',
      });

    useImperativeHandle(ref, () => ({
      handleValidation: (skipIfNull = false) =>
        handleMultiInputValidation(skipIfNull),
    }));

    const dispatch = useDispatch();

    const sendBlurLinkEvent = (fieldName: string) => {
      let linkName = '';
      let objectInteraction = '';

      switch (fieldName) {
        case KSP_FIELD_ID_1:
          linkName = ANALYTICS_LINK_NAME_KSP1;
          objectInteraction = ANALYTICS_OBJECTINTERACTION_KSP1;
          break;
        case KSP_FIELD_ID_2:
          linkName = ANALYTICS_LINK_NAME_KSP2;
          objectInteraction = ANALYTICS_OBJECTINTERACTION_KSP2;
          break;
        case KSP_FIELD_ID_3:
          linkName = ANALYTICS_LINK_NAME_KSP3;
          objectInteraction = ANALYTICS_OBJECTINTERACTION_KSP3;
          break;
        default:
          log.error(
            'KeySellingPoints | Unknown key selling point field id | sendBlurLinkEvent',
            {
              fieldName,
            },
          );
          break;
      }

      if (isPageAnalyticsEnabled) {
        const eventData: TrackingEvent = {
          eventName: KEY_SELLING_POINT_PRESSED,
          objectInteraction,
        };

        getTrackingAdapter.sendLinkEvent(eventData);
      } else {
        const eventData: TealiumEventData = {
          linkName,
          eventName: [EVENT_NAME_INTERACTION, linkName],
          objectInteraction,
        };

        if (eventData) {
          sendLinkEvent(tealium, eventData);
        }
      }
    };

    const handleMultiInputValidation = (skipIfNull = false): string[] => {
      let validators: Validators = [maxLength];
      if (isFilterContentEnabled) validators = [...validators, filterContent];
      if (isPerUnitBilling) validators = [...validators, notEmail];

      const validationRules = [
        {
          field: 'keySellingPoint1',
          validators,
          validatorProps: {
            max: KEY_SELLING_POINTS_MAX_LENGTH,
            country,
            sendTrackingEvents: sendContentSafetyTrackingEvents,
          },
          value: keySellingPoint1Input.value,
        },
        {
          field: 'keySellingPoint2',
          validators,
          validatorProps: {
            max: KEY_SELLING_POINTS_MAX_LENGTH,
            country,
            sendTrackingEvents: sendContentSafetyTrackingEvents,
          },
          value: keySellingPoint2Input.value,
        },
        {
          field: 'keySellingPoint3',
          validators,
          validatorProps: {
            max: KEY_SELLING_POINTS_MAX_LENGTH,
            country,
            sendTrackingEvents: sendContentSafetyTrackingEvents,
          },
          value: keySellingPoint3Input.value,
        },
      ];

      const validationResult = validateFields(
        validationRules,
        skipIfNull,
        getLanguage(),
        [sendPerUnitBillingValidationTrackingEvents],
      );

      const errors: any = [];

      if (validationResult?.keySellingPoint1) {
        setKeySellingPoint1Input({
          value: keySellingPoint1Input.value,
          isValid: false,
          message: validationResult?.keySellingPoint1,
        });
        errors.push(validationResult?.keySellingPoint1);
      }

      if (validationResult?.keySellingPoint2) {
        setKeySellingPoint2Input({
          value: keySellingPoint2Input.value,
          isValid: false,
          message: validationResult?.keySellingPoint2,
        });
        errors.push(validationResult?.keySellingPoint2);
      }

      if (validationResult?.keySellingPoint3) {
        setKeySellingPoint3Input({
          value: keySellingPoint3Input.value,
          isValid: false,
          message: validationResult?.keySellingPoint3,
        });
        errors.push(validationResult?.keySellingPoint3);
      }

      return errors;
    };

    const handleOnBlur =
      (fieldName: string) => async (event: FormEvent<HTMLInputElement>) => {
        const errorMessage = handleMultiInputValidation();

        if (errorMessage.length === 0) {
          const { value } = event.currentTarget;

          switch (fieldName) {
            case KSP_FIELD_ID_1:
              dispatch(actionUpdateKeySellingPoint1(value));
              break;
            case KSP_FIELD_ID_2:
              dispatch(actionUpdateKeySellingPoint2(value));
              break;
            case KSP_FIELD_ID_3:
              dispatch(actionUpdateKeySellingPoint3(value));
              break;

            default:
              log.error(
                'KeySellingPoints | Unknown key selling point field id | handleOnBlur',
                {
                  fieldName,
                },
              );
              break;
          }

          sendBlurLinkEvent(fieldName);
        }
      };

    const handleOnChange =
      (fieldName: string) => (event: FormEvent<HTMLInputElement>) => {
        const { value } = event.currentTarget;

        switch (fieldName) {
          case KSP_FIELD_ID_1:
            setKeySellingPoint1Input({ ...formFieldObject, value });
            break;
          case KSP_FIELD_ID_2:
            setKeySellingPoint2Input({ ...formFieldObject, value });
            break;
          case KSP_FIELD_ID_3:
            setKeySellingPoint3Input({ ...formFieldObject, value });
            break;
          default:
            log.error(
              'KeySellingPoints | Unknown key selling point field id | handleOnChange',
              {
                fieldName,
              },
            );
            break;
        }
      };

    return (
      <Box position="relative">
        <Stack space="small">
          <ProductFeatureBadge />
          <Stack space="xsmall">
            <FieldLabel
              htmlFor="keySellingPoint1"
              label={t('key-selling-points-label')}
              secondaryLabel={t('key-selling-points-secondary-label')}
              description={t('key-selling-points-description')}
            />
            <TextFieldWithCounter
              ariaLabel={`${t('key-selling-point-aria-label')} 1`}
              id="keySellingPoint1"
              value={keySellingPoint1Input.value}
              onBlur={handleOnBlur(KSP_FIELD_ID_1)}
              onChange={handleOnChange(KSP_FIELD_ID_1)}
              isValid={keySellingPoint1Input.isValid}
              message={keySellingPoint1Input.message}
              maxCharacters={KEY_SELLING_POINTS_MAX_LENGTH}
              data={{ 'hj-whitelist': 'true', testid: 'KeySellingPoint1' }}
            />
            <TextFieldWithCounter
              ariaLabel={`${t('key-selling-point-aria-label')} 2`}
              id="keySellingPoint2"
              value={keySellingPoint2Input.value}
              onBlur={handleOnBlur(KSP_FIELD_ID_2)}
              onChange={handleOnChange(KSP_FIELD_ID_2)}
              isValid={keySellingPoint2Input.isValid}
              message={keySellingPoint2Input.message}
              maxCharacters={KEY_SELLING_POINTS_MAX_LENGTH}
              data={{ 'hj-whitelist': 'true', testid: 'KeySellingPoint2' }}
            />
            <TextFieldWithCounter
              ariaLabel={`${t('key-selling-point-aria-label')} 3`}
              id="keySellingPoint3"
              value={keySellingPoint3Input.value}
              onBlur={handleOnBlur(KSP_FIELD_ID_3)}
              onChange={handleOnChange(KSP_FIELD_ID_3)}
              isValid={keySellingPoint3Input.isValid}
              message={keySellingPoint3Input.message}
              maxCharacters={KEY_SELLING_POINTS_MAX_LENGTH}
              data={{ 'hj-whitelist': 'true', testid: 'KeySellingPoint3' }}
            />
          </Stack>
        </Stack>
      </Box>
    );
  },
);
